import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Radio, MapPin, Plus, Save } from 'lucide-react'
import { api } from '../lib/api'
import { useAuth } from '../context/AuthContext'

// Notification-routing matrix (migration 0171, per 2026-08-27 monthly-
// meeting roadmap item 1). Super_admin + operations edit who receives
// which topic; per-topic rules can optionally be scoped to a venue for
// bookings that land against events tied to that venue.
//
// Rows: (topic, venue?) tuples. Every topic always has an "All venues"
// catch-all row; venue-specific rows appear beneath when at least one
// venue exists in the DB. Columns: every active admin, with a checkbox
// per cell. Save PUTs the entire rules array back atomically.
//
// Ops can also add a partner venue inline via the small form at the
// top so they don't have to bounce between screens the first time they
// wire a new one (Carakale Tasting Room, JR Winery, etc.).

interface Venue {
  id: string
  key: string
  name_en: string
  name_ar: string
  active: number
}

interface Admin {
  id: string
  name: string
  email: string
  role: string
}

interface Rule {
  event_type: string
  venue_id: string | null
  admin_id: string
}

interface RoutingResponse {
  topics: string[]
  venues: Venue[]
  admins: Admin[]
  rules: Rule[]
}

const TOPIC_LABELS: Record<string, { en: string; help: string }> = {
  new_support_ticket:   { en: 'New support ticket',      help: 'Customer opened a support message.' },
  new_booking:          { en: 'New event / experience booking', help: 'Ticket booked. Venue-scoped when the event is tied to a venue.' },
  new_catering_request: { en: 'New catering / bar-service enquiry', help: 'Customer submitted a catering form.' },
  large_order:          { en: 'Large order',              help: 'Delivery order above the value threshold in Settings.' },
  sla_breach_pos:       { en: 'POS SLA breach (critical)',help: 'Critical: always routes to on-call admins, never this table.' },
  payment_failed:       { en: 'Payment failed (critical)',help: 'Critical: always routes to on-call admins, never this table.' },
}

function ruleKey(eventType: string, venueId: string | null, adminId: string) {
  return `${eventType}|${venueId ?? ''}|${adminId}`
}

export default function NotificationsRouting() {
  const { can } = useAuth()
  const canManage = can(['super_admin', 'operations'])
  const qc = useQueryClient()

  const { data, isLoading } = useQuery<RoutingResponse>({
    queryKey: ['notification-routing'],
    queryFn: () => api.get('/notification-routing').then(r => r.data),
  })

  // Local editing state, hydrated from server on load.
  const [checked, setChecked] = useState<Set<string>>(new Set())
  const [dirty, setDirty] = useState(false)
  const [savedTick, setSavedTick] = useState(false)

  useEffect(() => {
    if (!data) return
    setChecked(new Set(data.rules.map(r => ruleKey(r.event_type, r.venue_id, r.admin_id))))
    setDirty(false)
  }, [data])

  const rows = useMemo(() => {
    if (!data) return []
    const out: { eventType: string; venueId: string | null; venueLabel: string }[] = []
    for (const t of data.topics) {
      out.push({ eventType: t, venueId: null, venueLabel: 'All venues' })
      for (const v of data.venues) {
        out.push({ eventType: t, venueId: v.id, venueLabel: v.name_en })
      }
    }
    return out
  }, [data])

  const toggle = (eventType: string, venueId: string | null, adminId: string) => {
    if (!canManage) return
    const k = ruleKey(eventType, venueId, adminId)
    const next = new Set(checked)
    if (next.has(k)) next.delete(k)
    else next.add(k)
    setChecked(next)
    setDirty(true)
  }

  const save = useMutation({
    mutationFn: async () => {
      const rules = Array.from(checked).map(k => {
        const [event_type, venue_raw, admin_id] = k.split('|')
        return { event_type, venue_id: venue_raw || null, admin_id }
      })
      await api.put('/notification-routing', { rules })
    },
    onSuccess: () => {
      setDirty(false)
      setSavedTick(true)
      setTimeout(() => setSavedTick(false), 1600)
      qc.invalidateQueries({ queryKey: ['notification-routing'] })
    },
  })

  // Inline venue quick-add.
  const [venueForm, setVenueForm] = useState({ key: '', name_en: '', name_ar: '' })
  const [venueError, setVenueError] = useState<string | null>(null)
  const addVenue = useMutation({
    mutationFn: async () => {
      setVenueError(null)
      await api.post('/venues', {
        key: venueForm.key.trim(),
        name_en: venueForm.name_en.trim(),
        name_ar: venueForm.name_ar.trim(),
      })
    },
    onSuccess: () => {
      setVenueForm({ key: '', name_en: '', name_ar: '' })
      qc.invalidateQueries({ queryKey: ['notification-routing'] })
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { message?: string } } }
      setVenueError(err.response?.data?.message ?? 'Could not create venue')
    },
  })

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <Radio size={20} className="text-[#c41e3a]" />
          Notification routing
        </h1>
        <p className="text-sm text-gray-500 mt-1 max-w-3xl">
          Route ops notifications to the right people. Each cell says "this admin should get notified for this topic (optionally when the event is tied to this venue)." Critical alerts (POS SLA breach, payment failed) always go to on-call admins and ignore this table. Per-admin channel choice (email / SMS) still lives in each admin's Profile → Notifications.
        </p>
      </div>

      {canManage && (
        <div className="bg-[#1a1a1a] rounded-xl border border-white/10 p-4">
          <div className="flex items-center gap-2 mb-3">
            <MapPin size={16} className="text-[#c41e3a]" />
            <h2 className="text-sm font-semibold text-white">Add a venue</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <input
              value={venueForm.key}
              onChange={e => setVenueForm(v => ({ ...v, key: e.target.value.toLowerCase() }))}
              placeholder="key (e.g. carakale-tasting)"
              className="bg-black/40 border border-white/10 rounded px-3 py-2 text-sm text-white"
            />
            <input
              value={venueForm.name_en}
              onChange={e => setVenueForm(v => ({ ...v, name_en: e.target.value }))}
              placeholder="Name (EN)"
              className="bg-black/40 border border-white/10 rounded px-3 py-2 text-sm text-white"
            />
            <input
              value={venueForm.name_ar}
              onChange={e => setVenueForm(v => ({ ...v, name_ar: e.target.value }))}
              placeholder="الاسم (AR)"
              dir="rtl"
              className="bg-black/40 border border-white/10 rounded px-3 py-2 text-sm text-white"
            />
            <button
              disabled={!venueForm.key.trim() || !venueForm.name_en.trim() || !venueForm.name_ar.trim() || addVenue.isPending}
              onClick={() => addVenue.mutate()}
              className="flex items-center justify-center gap-2 px-4 py-2 rounded bg-[#c41e3a] text-white text-sm font-semibold hover:bg-[#a01830] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Plus size={14} />
              {addVenue.isPending ? 'Adding…' : 'Add venue'}
            </button>
          </div>
          {venueError && <p className="text-xs text-red-400 mt-2">{venueError}</p>}
        </div>
      )}

      <div className="bg-[#1a1a1a] rounded-xl border border-white/10 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-sm text-gray-500">Loading…</div>
        ) : !data ? (
          <div className="p-8 text-center text-sm text-gray-500">Could not load routing.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/5 text-xs uppercase tracking-wide text-gray-500 sticky top-0">
                <tr>
                  <th className="text-left px-4 py-3 font-medium min-w-[220px]">Topic</th>
                  <th className="text-left px-4 py-3 font-medium min-w-[160px]">Venue</th>
                  {data.admins.map(a => (
                    <th key={a.id} className="px-3 py-3 font-medium text-center whitespace-nowrap">
                      <div className="text-white/90">{a.name}</div>
                      <div className="text-[10px] text-gray-500 font-normal">{a.role}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => {
                  const meta = TOPIC_LABELS[row.eventType] ?? { en: row.eventType, help: '' }
                  const isCritical = row.eventType === 'sla_breach_pos' || row.eventType === 'payment_failed'
                  const firstOfTopic = row.venueId === null
                  return (
                    <tr key={i} className={firstOfTopic ? 'border-t border-white/10' : ''}>
                      <td className="px-4 py-2 align-top">
                        {firstOfTopic && (
                          <div>
                            <div className="text-white font-medium">{meta.en}</div>
                            <div className="text-[11px] text-gray-500 mt-0.5">{meta.help}</div>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2 align-top text-gray-400">
                        {row.venueLabel}
                      </td>
                      {data.admins.map(a => {
                        const on = checked.has(ruleKey(row.eventType, row.venueId, a.id))
                        return (
                          <td key={a.id} className="px-3 py-2 text-center">
                            <input
                              type="checkbox"
                              disabled={!canManage || isCritical}
                              checked={on}
                              onChange={() => toggle(row.eventType, row.venueId, a.id)}
                              className="w-4 h-4 accent-[#c41e3a] disabled:opacity-30 disabled:cursor-not-allowed"
                            />
                          </td>
                        )
                      })}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        {canManage && (
          <div className="flex items-center justify-end gap-3 px-4 py-3 border-t border-white/10 bg-black/30">
            {savedTick && <span className="text-xs text-green-400">Saved</span>}
            {dirty && !save.isPending && <span className="text-xs text-amber-400">Unsaved changes</span>}
            <button
              disabled={!dirty || save.isPending}
              onClick={() => save.mutate()}
              className="flex items-center gap-2 px-4 py-2 rounded bg-[#c41e3a] text-white text-sm font-semibold hover:bg-[#a01830] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Save size={14} />
              {save.isPending ? 'Saving…' : 'Save routing'}
            </button>
          </div>
        )}
      </div>

      <div className="text-xs text-gray-500 max-w-3xl">
        <strong className="text-gray-400">How this works.</strong> When ops has NOT configured any rule for a topic, notifications fall back to today's behaviour: every active admin who has that topic enabled in their Profile → Notifications gets pinged. Once you tick any admin for a topic here, only the ticked admins receive that topic going forward. Venue rows are UNIONed with the "All venues" row — an admin ticked in both gets one notification, not two.
      </div>
    </div>
  )
}
